import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LIMITS, LimitsConfig, loadLimits } from './limits';

type LimitsContextValue = {
  limits: LimitsConfig;
  refresh: () => Promise<void>;
};

const LimitsContext = createContext<LimitsContextValue | undefined>(undefined);

const STORAGE_KEY = 'limitsConfig';

function getLimitsUrl() {
  return process.env.REACT_APP_LIMITS_URL || '';
}

function readCachedLimits(): LimitsConfig | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LimitsConfig;
  } catch {
    return null;
  }
}

function writeCachedLimits(limits: LimitsConfig) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limits));
  } catch {
    // ignore
  }
}

export const LimitsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [limits, setLimits] = useState<LimitsConfig>(() => readCachedLimits() ?? DEFAULT_LIMITS);

  const refresh = async () => {
    const next = await loadLimits(getLimitsUrl());
    setLimits(next);
    writeCachedLimits(next);
  };

  useEffect(() => {
    // Always attempt to refresh once on app start.
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ limits, refresh }), [limits]);

  return <LimitsContext.Provider value={value}>{children}</LimitsContext.Provider>;
};

export function useLimits(): LimitsContextValue {
  const ctx = useContext(LimitsContext);
  if (!ctx) throw new Error('useLimits must be used within LimitsProvider');
  return ctx;
}
